'use client';

import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ds';
import { AuroraField } from '@/components/onboarding/AuroraField';
import { SlikerrLockup } from '@/components/onboarding/SlikerrLockup';
// Side-effect: initialise i18next. `not-found.tsx` for a fully unmatched URL is
// NOT guaranteed to render inside `providers.tsx` (which normally does this), and
// an uninitialised i18next makes `t()` echo the raw key back.
import '@/lib/i18n';
import styles from './not-found.module.css';

/**
 * Global 404: Next.js renders this for any unmatched URL, and for any future
 * `notFound()` call that no nearer `not-found.tsx` catches.
 *
 * It is NOT what a visitor sees for a missing or unpublished business today.
 * `(public)/b/[slug]` is a client page that resolves the slug itself, answers
 * 200, and renders its own empty state - the "מוסתר - דף ההזמנה מוחזיר 404"
 * copy in the dashboard describes the API's response, not this route. Verified
 * against a running dev server, not inferred.
 *
 * ## Why it borrows the wizard's intro rather than owning a look
 *
 * `AuroraField` and `SlikerrLockup` are imported from `components/onboarding/`,
 * not copied: this page and `/business-onboarding`'s intro are the two
 * full-bleed brand surfaces on web, and the house rule is that "exactly the
 * same" means one component. A dead end and the front door should be visibly
 * the same product. If the intro's aurora or lockup changes, this page inherits
 * it — which is the point. The one thing NOT shared is the intro's headline
 * scale, which lives in `onboarding.module.css`; the matching values here carry
 * a comment saying so.
 *
 * Client component, but a DELIBERATELY Hebrew-only one. The `import '@/lib/i18n'`
 * above is what makes `t()` resolve at all: an unmatched URL does not render
 * inside `providers.tsx`, so neither i18next's init nor `LocaleRestorer` runs
 * here. The consequence, measured rather than assumed: a visitor who has saved
 * `slikerr_locale = 'en'` still gets Hebrew copy at `dir="rtl"` on this page.
 * That is acceptable for a Hebrew-first product (`/offline` is hardcoded Hebrew
 * outright) - but it is a real gap, not a claim that the toggle works here. To
 * close it, this page would have to read `localStorage` itself; changing the
 * import alone will not do it.
 */
export default function NotFound() {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <main className={styles.page}>
      <AuroraField />

      {/* ONE block: lockup, numeral, message, action — centred as a whole, so
          the space above it and below it is equal. The lockup is NOT pinned to
          the top edge; it belongs to the numeral it introduces, and pinning it
          left the composition top-heavy with the free space unevenly split. */}
      <div className={styles.inner}>
        <div className={styles.statement}>
          <header className={styles.crest}>
            <SlikerrLockup />
            <p className={styles.numeral} role="img" aria-label={t('not_found.code_label')}>
              404
            </p>
          </header>

          <div className={styles.message}>
            <h1 className={styles.title}>{t('not_found.title')}</h1>
            <p className={styles.body}>{t('not_found.body')}</p>
          </div>

          {/* One action, like the wizard intro. A "back" secondary was here and came
              off: from a 404 the previous page is often the broken link itself, so
              it is an offer to fail again next to an offer that works. */}
          <div className={styles.actions}>
            <Button onClick={() => router.push('/')}>{t('not_found.home')}</Button>
          </div>
        </div>
      </div>
    </main>
  );
}
